import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

export type SplitDistributionBucket = {
  bucketLabel: string;
  count: number;
  bucketStartSeconds: number;
  bucketEndSeconds: number;
};

export type SplitDistributionSummary = {
  sampleSize: number;
  minTimeSeconds: number;
  maxTimeSeconds: number;
  avgTimeSeconds: number;
  medianTimeSeconds: number;
  percentile25TimeSeconds: number;
  percentile75TimeSeconds: number;
  buckets: SplitDistributionBucket[];
};

type SplitFastestRecord = {
  athleteName: string;
  timeFormatted: string;
  year?: number | null;
};

export type SplitDistributionRecord = {
  segmentType: string;
  fastest: SplitFastestRecord | null;
  avgTimeFormatted: string | null;
  distanceKm?: number | null;
  distribution: SplitDistributionSummary;
};

type SplitDistributionChartProps = {
  ageGroups: string[];
  selectedAgeGroup: string;
  onAgeGroupChange: (value: string) => void;
  splits?: SplitDistributionRecord[];
  isFetching?: boolean;
  title?: string;
  description?: string;
};

type DisplayMode = "time" | "converted";

const splitColors: Record<string, string> = {
  swim: "var(--d8-cyan)",
  bike: "#f59e0b",
  run: "var(--d8-pink)",
  finish: "#22c55e",
};

function formatSegmentLabel(value: string): string {
  if (!value) return "Split";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDuration(seconds: number): string {
  const safeSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

function formatDurationShort(seconds: number): string {
  const safeSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
  }

  return `${minutes}m`;
}

// ── Pace / speed conversion helpers ────────────────────────────────────────

/** Returns true when the segment should show speed (km/h) instead of pace. */
function isSpeedSegment(segmentType: string): boolean {
  return segmentType === "bike";
}

/** Convert a finish/split time to pace in seconds-per-km. */
function timeToPaceSecondsPerKm(
  timeSeconds: number,
  distanceKm: number,
): number {
  if (distanceKm <= 0) return 0;
  return timeSeconds / distanceKm;
}

/** Convert a finish/split time to speed in km/h. */
function timeToSpeedKph(timeSeconds: number, distanceKm: number): number {
  if (timeSeconds <= 0) return 0;
  return (distanceKm / timeSeconds) * 3600;
}

/** Format pace (seconds-per-km) as "X:XX /km". */
function formatPace(paceSecondsPerKm: number): string {
  const safePace = Math.max(0, Math.round(paceSecondsPerKm));
  const minutes = Math.floor(safePace / 60);
  const secs = safePace % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")} /km`;
}

/** Short pace label for axis ticks — no "/km" suffix. */
function formatPaceShort(paceSecondsPerKm: number): string {
  const safePace = Math.max(0, Math.round(paceSecondsPerKm));
  const minutes = Math.floor(safePace / 60);
  const secs = safePace % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

/** Format speed as "XX.X km/h". */
function formatSpeed(kph: number): string {
  return `${kph.toFixed(1)} km/h`;
}

/** Short speed label for axis ticks. */
function formatSpeedShort(kph: number): string {
  return `${kph.toFixed(1)}`;
}

/** Label for the converted-mode toggle button. */
function convertedModeLabel(segmentType: string): string {
  return isSpeedSegment(segmentType) ? "Speed" : "Pace";
}

/** Convert a time in seconds to the display value for the given segment. */
function timeToConverted(
  timeSeconds: number,
  distanceKm: number,
  segmentType: string,
): number {
  return isSpeedSegment(segmentType)
    ? timeToSpeedKph(timeSeconds, distanceKm)
    : timeToPaceSecondsPerKm(timeSeconds, distanceKm);
}

/** Format a converted value (pace or speed) for the given segment type. */
function formatConverted(value: number, segmentType: string): string {
  return isSpeedSegment(segmentType) ? formatSpeed(value) : formatPace(value);
}

/** Short-format a converted value for axis ticks. */
function formatConvertedShort(value: number, segmentType: string): string {
  return isSpeedSegment(segmentType)
    ? formatSpeedShort(value)
    : formatPaceShort(value);
}

/** Axis unit suffix shown alongside ticks. */
function convertedAxisUnit(segmentType: string): string {
  return isSpeedSegment(segmentType) ? "km/h" : "/km";
}

// ───────────────────────────────────────────────────────────────────────────

export function SplitDistributionChart({
  ageGroups,
  selectedAgeGroup,
  onAgeGroupChange,
  splits,
  isFetching = false,
  title = "Age Group Split Distribution",
  description = "Pick an age group and split to see how performances are distributed.",
}: SplitDistributionChartProps) {
  const [activeSplit, setActiveSplit] = useState("");
  const [displayMode, setDisplayMode] = useState<DisplayMode>("time");
  const orderedSplits = useMemo(() => splits ?? [], [splits]);

  useEffect(() => {
    if (orderedSplits.length === 0) {
      setActiveSplit("");
      return;
    }

    if (!orderedSplits.some((split) => split.segmentType === activeSplit)) {
      setActiveSplit(orderedSplits[0]?.segmentType ?? "");
    }
  }, [orderedSplits, activeSplit]);

  const selectedSplit =
    orderedSplits.find((split) => split.segmentType === activeSplit) ??
    orderedSplits[0] ??
    null;

  const canConvert =
    selectedSplit != null &&
    typeof selectedSplit.distanceKm === "number" &&
    selectedSplit.distanceKm > 0;

  // Reset to time mode when switching to a segment without distance data
  useEffect(() => {
    if (!canConvert && displayMode === "converted") {
      setDisplayMode("time");
    }
  }, [canConvert, displayMode]);

  const distanceKm = canConvert ? (selectedSplit.distanceKm as number) : 0;
  const segmentType = selectedSplit?.segmentType ?? "";
  const showConverted = displayMode === "converted" && canConvert;

  const chartData = useMemo(() => {
    if (!selectedSplit) return [];

    return selectedSplit.distribution.buckets.map((bucket) => {
      const midpointSeconds = Math.round(
        (bucket.bucketStartSeconds + bucket.bucketEndSeconds) / 2,
      );

      if (showConverted) {
        const convertedStart = timeToConverted(
          bucket.bucketStartSeconds,
          distanceKm,
          segmentType,
        );
        const convertedEnd = timeToConverted(
          bucket.bucketEndSeconds,
          distanceKm,
          segmentType,
        );
        const convertedMidpoint = timeToConverted(
          midpointSeconds,
          distanceKm,
          segmentType,
        );

        // For pace, start > end (slower time = higher pace number).
        // For speed, start > end already (faster time = higher speed).
        const rangeLabel = isSpeedSegment(segmentType)
          ? `${formatConverted(convertedEnd, segmentType)} - ${formatConverted(convertedStart, segmentType)}`
          : `${formatConverted(convertedStart, segmentType)} - ${formatConverted(convertedEnd, segmentType)}`;

        return {
          ...bucket,
          midpointSeconds,
          convertedMidpoint,
          convertedLabel: rangeLabel,
        };
      }

      return {
        ...bucket,
        midpointSeconds,
        convertedMidpoint: 0,
        convertedLabel: "",
      };
    });
  }, [selectedSplit, showConverted, distanceKm, segmentType]);

  const activeColor = selectedSplit
    ? splitColors[selectedSplit.segmentType] ?? "var(--d8-cyan)"
    : "var(--d8-cyan)";

  const chartConfig = {
    count: {
      label: "Athletes",
      color: activeColor,
    },
  } satisfies ChartConfig;

  // ── Rendering helpers for stat cards ──────────────────────────────────

  function renderTimeOrConverted(seconds: number): string {
    if (showConverted) {
      return formatConverted(
        timeToConverted(seconds, distanceKm, segmentType),
        segmentType,
      );
    }
    return formatDuration(seconds);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-medium">{title}</h2>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <Combobox
          items={ageGroups}
          value={selectedAgeGroup || null}
          onValueChange={(value) => onAgeGroupChange(value ?? "")}
        >
          <ComboboxInput
            aria-label="Select age group for split distribution"
            placeholder="Select age group..."
            className="w-full sm:w-[240px]"
            showClear={Boolean(selectedAgeGroup)}
          />
          <ComboboxContent>
            <ComboboxEmpty>No age groups found.</ComboboxEmpty>
            <ComboboxList>
              {(ag) => (
                <ComboboxItem key={ag} value={ag}>
                  {ag}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>

      {selectedAgeGroup ? (
        selectedSplit ? (
          <div
            className={cn(
              "space-y-3 rounded-lg border p-3 transition-opacity",
              isFetching ? "opacity-60" : "opacity-100",
            )}
          >
            <div className="flex flex-wrap items-center gap-2">
              {orderedSplits.map((split) => {
                const isActive = split.segmentType === selectedSplit.segmentType;
                const color = splitColors[split.segmentType] ?? "var(--d8-cyan)";

                return (
                  <button
                    key={split.segmentType}
                    type="button"
                    onClick={() => setActiveSplit(split.segmentType)}
                    className={cn(
                      "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                      isActive
                        ? "border-transparent text-black"
                        : "border-border bg-background text-muted-foreground hover:text-foreground",
                    )}
                    style={isActive ? { backgroundColor: color } : undefined}
                  >
                    {formatSegmentLabel(split.segmentType)}
                  </button>
                );
              })}

              {canConvert ? (
                <div className="ml-auto flex items-center rounded-md border border-border text-xs">
                  <button
                    type="button"
                    onClick={() => setDisplayMode("time")}
                    className={cn(
                      "rounded-l-md px-2.5 py-1 font-medium transition-colors",
                      displayMode === "time"
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    Time
                  </button>
                  <button
                    type="button"
                    onClick={() => setDisplayMode("converted")}
                    className={cn(
                      "rounded-r-md px-2.5 py-1 font-medium transition-colors",
                      displayMode === "converted"
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {convertedModeLabel(selectedSplit.segmentType)}
                  </button>
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
              <div className="rounded-md border bg-muted/30 px-3 py-2">
                <p className="text-muted-foreground">Fastest</p>
                <p className="font-mono font-semibold">
                  {showConverted && selectedSplit.distribution.minTimeSeconds > 0
                    ? renderTimeOrConverted(
                        selectedSplit.distribution.minTimeSeconds,
                      )
                    : (selectedSplit.fastest?.timeFormatted ?? "N/A")}
                </p>
                {selectedSplit.fastest ? (
                  <p className="truncate text-muted-foreground">
                    {selectedSplit.fastest.athleteName}
                    {selectedSplit.fastest.year
                      ? ` \u00b7 ${selectedSplit.fastest.year}`
                      : ""}
                  </p>
                ) : null}
              </div>
              <div className="rounded-md border bg-muted/30 px-3 py-2">
                <p className="text-muted-foreground">Average</p>
                <p className="font-mono font-semibold">
                  {showConverted
                    ? renderTimeOrConverted(
                        selectedSplit.distribution.avgTimeSeconds,
                      )
                    : (selectedSplit.avgTimeFormatted ??
                      formatDuration(
                        selectedSplit.distribution.avgTimeSeconds,
                      ))}
                </p>
              </div>
              <div className="rounded-md border bg-muted/30 px-3 py-2">
                <p className="text-muted-foreground">Median</p>
                <p className="font-mono font-semibold">
                  {renderTimeOrConverted(
                    selectedSplit.distribution.medianTimeSeconds,
                  )}
                </p>
              </div>
              <div className="rounded-md border bg-muted/30 px-3 py-2">
                <p className="text-muted-foreground">Middle 50%</p>
                <p className="font-mono font-semibold">
                  {renderTimeOrConverted(
                    selectedSplit.distribution.percentile25TimeSeconds,
                  )}{" "}
                  -{" "}
                  {renderTimeOrConverted(
                    selectedSplit.distribution.percentile75TimeSeconds,
                  )}
                </p>
              </div>
              <div className="rounded-md border bg-muted/30 px-3 py-2">
                <p className="text-muted-foreground">Sample</p>
                <p className="font-mono font-semibold">
                  {selectedSplit.distribution.sampleSize.toLocaleString()}
                </p>
              </div>
            </div>

            {chartData.length > 0 ? (
              <ChartContainer config={chartConfig} className="min-h-[280px]">
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 12, left: 2, bottom: 8 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey={
                      showConverted ? "convertedMidpoint" : "midpointSeconds"
                    }
                    tickLine={false}
                    axisLine={false}
                    minTickGap={14}
                    reversed={
                      showConverted && !isSpeedSegment(segmentType)
                    }
                    tickFormatter={(value) => {
                      if (showConverted) {
                        return formatConvertedShort(
                          Number(value),
                          segmentType,
                        );
                      }
                      return formatDurationShort(Number(value));
                    }}
                    label={
                      showConverted
                        ? {
                            value: convertedAxisUnit(segmentType),
                            position: "insideBottomRight",
                            offset: -4,
                            className: "fill-muted-foreground text-[10px]",
                          }
                        : undefined
                    }
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    width={42}
                  />
                  <ChartTooltip
                    cursor={{ fill: "hsl(var(--muted) / 0.3)" }}
                    content={
                      <ChartTooltipContent
                        labelFormatter={(_label, payload) => {
                          const row = payload?.[0]?.payload as
                            | {
                                bucketLabel?: string;
                                convertedLabel?: string;
                              }
                            | undefined;

                          if (showConverted && row?.convertedLabel) {
                            return `Range ${row.convertedLabel}`;
                          }
                          return `Range ${row?.bucketLabel ?? ""}`;
                        }}
                        formatter={(value) =>
                          `${Number(value ?? 0).toLocaleString()} athletes`
                        }
                      />
                    }
                  />
                  <Bar
                    dataKey="count"
                    name="Athletes"
                    fill="var(--color-count)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
            ) : (
              <p className="text-sm text-muted-foreground">
                No bucketed split data available for {selectedAgeGroup}.
              </p>
            )}
          </div>
        ) : !isFetching ? (
          <p className="text-sm text-muted-foreground">
            No split data available for {selectedAgeGroup}.
          </p>
        ) : null
      ) : (
        <p className="text-sm text-muted-foreground">
          Select an age group to view split distribution.
        </p>
      )}
    </div>
  );
}
